import { useTranslation } from "react-i18next";
import { ChangeMemberRoleDialog } from "@/shared/organizations/ChangeMemberRoleDialog";
import { summarizeAssignmentResults } from "@/shared/organizations/assignment-results";
import type { Notice } from "@/shared/organizations/InlineNotice";
import { ManageMemberLocationsDialog } from "@/shared/organizations/ManageMemberLocationsDialog";
import { ReactivateMemberDialog } from "@/shared/organizations/ReactivateMemberDialog";
import type { MemberAction } from "./member-actions";
import type { AdminOrganizationMember } from "./types";

export interface MemberDialogTarget {
  readonly action: MemberAction;
  readonly member: AdminOrganizationMember;
}

interface MemberDialogsProps {
  readonly organizationId: string;
  readonly target: MemberDialogTarget | null;
  /** `org.members.manage`: moving an ADMIN to STAFF picks locations, which needs it. */
  readonly canManageMembers: boolean;
  readonly onClose: () => void;
  /** Result of an action (or why it no longer applies), shown by the host next to fresh data. */
  readonly onNotice: (notice: Notice) => void;
}

/**
 * The one dialog of the roster row action in progress. Each dialog refreshes
 * the reads itself; this only routes its outcome to a page notice.
 */
export function MemberDialogs({
  organizationId,
  target,
  canManageMembers,
  onClose,
  onNotice,
}: MemberDialogsProps) {
  const { t } = useTranslation();
  if (!target) return null;
  const { action, member } = target;
  const common = {
    open: true,
    onOpenChange: (open: boolean) => {
      if (!open) onClose();
    },
    organizationId,
    membershipId: member.membershipId,
    memberName: member.user.displayName,
    onStale: (message: string) => onNotice({ tone: "error", message }),
  };

  if (action === "changeRole" && (member.role === "ADMIN" || member.role === "STAFF")) {
    return (
      <ChangeMemberRoleDialog
        {...common}
        currentRole={member.role}
        canAssignLocations={canManageMembers}
        onDone={(newRole) => {
          onNotice({
            tone: "success",
            message: t(`organizationAccess.changeRole.success.${newRole}`),
          });
          onClose();
        }}
      />
    );
  }
  if (action === "reactivate" && (member.role === "ADMIN" || member.role === "STAFF")) {
    return (
      <ReactivateMemberDialog
        {...common}
        role={member.role}
        onDone={({ changed, role }) =>
          onNotice({
            tone: "success",
            message: t(
              changed
                ? `organizationAccess.reactivate.success.${role}`
                : "organizationAccess.reactivate.alreadyActive",
            ),
          })
        }
      />
    );
  }
  if (action === "manageLocations" && member.role === "STAFF") {
    return (
      <ManageMemberLocationsDialog
        {...common}
        userId={member.user.id}
        onDone={(results) =>
          onNotice({
            tone: "success",
            message: t(
              summarizeAssignmentResults(results) === "changed"
                ? "organizationAccess.manageLocations.success"
                : "organizationAccess.manageLocations.noChanges",
            ),
          })
        }
      />
    );
  }
  return null;
}
